import { classifyLayerCategory } from '../siteParcelClassification';
import proj4 from 'proj4';
import type { CadGeoreference, CadImportPayload, CadLocalFeature, CadPoint } from '../../types/cad';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';

const EARTH_RADIUS_M = 6_378_137;
const MIN_COS_LATITUDE = 1e-8;

function finite(value: number, label: string): number {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite.`);
  return value;
}

function validateGeoreference(georef: CadGeoreference): void {
  finite(georef.anchorLat, 'CAD anchor latitude');
  finite(georef.anchorLng, 'CAD anchor longitude');
  finite(georef.originX, 'CAD origin X');
  finite(georef.originY, 'CAD origin Y');
  finite(georef.rotationDeg, 'CAD rotation');
  if (georef.anchorLat < -90 || georef.anchorLat > 90) throw new Error('CAD anchor latitude must be between -90 and 90 degrees.');
  if (georef.anchorLng < -180 || georef.anchorLng > 180) throw new Error('CAD anchor longitude must be between -180 and 180 degrees.');
  if (!Number.isFinite(georef.metersPerUnit) || georef.metersPerUnit <= 0) throw new Error('CAD scale must be greater than zero.');
  if (georef.projectedCrs) {
    finite(georef.projectedCrs.referenceXM, 'CAD projected reference X');
    finite(georef.projectedCrs.referenceYM, 'CAD projected reference Y');
    if (!georef.projectedCrs.definition.trim()) throw new Error('CAD projected coordinate-system definition is empty.');
  }
}

export function cadLayerCategory(layerName: string): SiteLayerCategory {
  const text = layerName.trim().toLowerCase();
  if (/\b(?:co2|gas|oil|lpg)\s*pipeline\b|\bpipeline\b/.test(text)) return 'infrastructure.pipeline';
  if (/\bzone\s*3\b|carbon\s*capture|\bccs\b|adsorber|absorber|stripper|solvent|co2\s*(?:compression|metering)|dehydration/.test(text)) return 'concept.ccs';
  if (/\bzone\s*2\b|gas\s*turbine|steam\s*turbine|\bhrsg\b|air[- ]cooled\s*condenser|power\s*(?:plant|block|island)/.test(text)) return 'concept.generation';
  if (/\bzone\s*4\b|switchyard|sub\s*station|transformer|\b\d+\s*kv\b|electrical|cable\s*corridor/.test(text)) return 'infrastructure.electrical';
  if (/fiber/.test(text)) return 'infrastructure.fiber';
  if (/road|access|parking|truck\s*staging|sidewalk|loading\s*dock/.test(text)) return 'infrastructure.transport';
  if (/setback|wetland|flood|retention\s*(?:basin|pond)|detention\s*pond/.test(text)) return 'constraint.environmental';
  return classifyLayerCategory(layerName, []);
}

export function cadPointToLatLng(point: CadPoint, georef: CadGeoreference): { lat: number; lng: number } {
  validateGeoreference(georef);
  finite(point.x, 'CAD X coordinate');
  finite(point.y, 'CAD Y coordinate');

  const theta = georef.rotationDeg * Math.PI / 180;
  const dx = (point.x - georef.originX) * georef.metersPerUnit;
  const dy = (point.y - georef.originY) * georef.metersPerUnit;
  const eastM = dx * Math.cos(theta) - dy * Math.sin(theta);
  const northM = dx * Math.sin(theta) + dy * Math.cos(theta);
  if (georef.projectedCrs) {
    const [lng, lat] = proj4(
      georef.projectedCrs.definition,
      'EPSG:4326',
      [georef.projectedCrs.referenceXM + eastM, georef.projectedCrs.referenceYM + northM],
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new Error(`CAD coordinate transformation failed for ${georef.projectedCrs.name}.`);
    }
    return { lat, lng };
  }
  const anchorLatRad = georef.anchorLat * Math.PI / 180;
  const cosLat = Math.max(MIN_COS_LATITUDE, Math.abs(Math.cos(anchorLatRad)));

  return {
    lat: georef.anchorLat + northM / EARTH_RADIUS_M * 180 / Math.PI,
    lng: georef.anchorLng + eastM / (EARTH_RADIUS_M * cosLat) * 180 / Math.PI,
  };
}

export function latLngToCadPoint(point: { lat: number; lng: number }, georef: CadGeoreference): CadPoint {
  validateGeoreference(georef);
  finite(point.lat, 'Latitude');
  finite(point.lng, 'Longitude');
  let eastM: number;
  let northM: number;
  if (georef.projectedCrs) {
    const [projectedX, projectedY] = proj4(
      'EPSG:4326',
      georef.projectedCrs.definition,
      [point.lng, point.lat],
    );
    eastM = projectedX - georef.projectedCrs.referenceXM;
    northM = projectedY - georef.projectedCrs.referenceYM;
  } else {
    const anchorLatRad = georef.anchorLat * Math.PI / 180;
    eastM = (point.lng - georef.anchorLng) * Math.PI / 180 * EARTH_RADIUS_M * Math.max(MIN_COS_LATITUDE, Math.abs(Math.cos(anchorLatRad)));
    northM = (point.lat - georef.anchorLat) * Math.PI / 180 * EARTH_RADIUS_M;
  }
  const theta = georef.rotationDeg * Math.PI / 180;
  const dx = eastM * Math.cos(theta) + northM * Math.sin(theta);
  const dy = -eastM * Math.sin(theta) + northM * Math.cos(theta);
  return {
    x: georef.originX + dx / georef.metersPerUnit,
    y: georef.originY + dy / georef.metersPerUnit,
  };
}

export function defaultCadGeoreference(
  payload: CadImportPayload,
  _siteAnchor: { lat: number; lng: number } | null,
): CadGeoreference | null {
  return payload.suggestedGeoreference;
}

/** Explicit user choice for drawings that do not contain a supported CRS. */
export function cadGeoreferenceAtSiteAnchor(
  payload: CadImportPayload,
  siteAnchor: { lat: number; lng: number },
): CadGeoreference {
  return {
    anchorLat: siteAnchor.lat,
    anchorLng: siteAnchor.lng,
    originX: (payload.bounds.minX + payload.bounds.maxX) / 2,
    originY: (payload.bounds.minY + payload.bounds.maxY) / 2,
    metersPerUnit: payload.drawingMetersPerUnit ?? 1,
    rotationDeg: 0,
    source: 'site-anchor',
  };
}

function minimumCoordinateCount(feature: CadLocalFeature): number {
  if (feature.geometry.type === 'point') return 1;
  if (feature.geometry.type === 'line') return 2;
  return 3;
}

export function cadImportToSiteLayers(
  payload: CadImportPayload,
  georef: CadGeoreference,
  selectedCadLayers?: ReadonlySet<string>,
): SiteLayer[] {
  validateGeoreference(georef);
  const importedAt = new Date().toISOString();
  const sourcePrefix = payload.sourceSha256.slice(0, 12);
  return payload.features.flatMap((feature) => {
    if (selectedCadLayers && !selectedCadLayers.has(feature.layer)) return [];
    if (feature.geometry.coordinates.length < minimumCoordinateCount(feature)) return [];
    const coordinates = feature.geometry.coordinates.map((point) => cadPointToLatLng(point, georef));
    const category = feature.category ?? cadLayerCategory(feature.layer);
    const attributes: NonNullable<SiteLayer['attributes']> = {
      cadSourceFile: payload.fileName,
      cadSourceSha256: payload.sourceSha256,
      cadDwgVersion: payload.dwgVersion,
      cadEntityHandle: feature.entityHandle,
      cadEntityType: feature.entityType,
      cadLayer: feature.layer,
      cadAnchorLat: georef.anchorLat,
      cadAnchorLng: georef.anchorLng,
      cadOriginX: georef.originX,
      cadOriginY: georef.originY,
      cadMetersPerUnit: georef.metersPerUnit,
      cadDrawingInsUnitsCode: payload.drawingInsUnitsCode,
      cadRotationDeg: georef.rotationDeg,
      cadGeoreferenceSource: georef.source,
    };
    if (georef.projectedCrs) {
      attributes.cadCrsCode = georef.projectedCrs.code;
      attributes.cadCrsName = georef.projectedCrs.name;
      attributes.cadCrsDefinition = georef.projectedCrs.definition;
      attributes.cadProjectedReferenceXM = georef.projectedCrs.referenceXM;
      attributes.cadProjectedReferenceYM = georef.projectedCrs.referenceYM;
    }
    if (feature.colorIndex != null) attributes.cadColorIndex = feature.colorIndex;
    return [{
      id: `cad-${sourcePrefix}-${feature.entityHandle}`,
      name: feature.layer || `CAD entity ${feature.entityHandle}`,
      category,
      geometry: { type: feature.geometry.type, coordinates },
      description: `Editable CAD overlay from ${payload.fileName}; source layer ${feature.layer || '0'}.`,
      source: 'cad-import' as const,
      importedAt,
      visible: true,
      attributes,
    }];
  });
}

export interface MergeCadImportedLayersResult {
  merged: SiteLayer[];
  added: SiteLayer[];
  alreadyPresent: SiteLayer[];
}

/**
 * Merge a staged DWG overlay by its deterministic CAD entity identity.
 *
 * CAD drawings may intentionally contain distinct entity handles with the
 * same layer name and identical geometry (for example coincident equipment
 * points). The KMZ merge path also compares content hashes, which would
 * collapse those valid CAD entities. CAD ids include source SHA + entity
 * handle, so id equality is the only safe duplicate signal here.
 */
export function mergeCadImportedLayers(
  existing: readonly SiteLayer[],
  incoming: readonly SiteLayer[],
): MergeCadImportedLayersResult {
  const merged = [...existing];
  const seenIds = new Set(existing.map((layer) => layer.id));
  const added: SiteLayer[] = [];
  const alreadyPresent: SiteLayer[] = [];

  for (const layer of incoming) {
    if (seenIds.has(layer.id)) {
      alreadyPresent.push(layer);
      continue;
    }
    seenIds.add(layer.id);
    merged.push(layer);
    added.push(layer);
  }

  return { merged, added, alreadyPresent };
}

export function georeferenceFromLayer(layer: SiteLayer): CadGeoreference | null {
  const a = layer.attributes;
  if (!a) return null;
  const anchorLat = a.cadAnchorLat;
  const anchorLng = a.cadAnchorLng;
  const originX = a.cadOriginX;
  const originY = a.cadOriginY;
  const metersPerUnit = a.cadMetersPerUnit;
  const rotationDeg = a.cadRotationDeg;
  if (![anchorLat, anchorLng, originX, originY, metersPerUnit, rotationDeg].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  return {
    anchorLat: anchorLat as number,
    anchorLng: anchorLng as number,
    originX: originX as number,
    originY: originY as number,
    metersPerUnit: metersPerUnit as number,
    rotationDeg: rotationDeg as number,
    source: a.cadGeoreferenceSource === 'embedded'
      ? 'embedded'
      : a.cadGeoreferenceSource === 'manual'
        ? 'manual'
        : 'site-anchor',
    ...(typeof a.cadCrsDefinition === 'string'
      && typeof a.cadCrsName === 'string'
      && typeof a.cadProjectedReferenceXM === 'number'
      && typeof a.cadProjectedReferenceYM === 'number'
      ? {
          projectedCrs: {
            code: typeof a.cadCrsCode === 'string' ? a.cadCrsCode : null,
            name: a.cadCrsName,
            definition: a.cadCrsDefinition,
            referenceXM: a.cadProjectedReferenceXM,
            referenceYM: a.cadProjectedReferenceYM,
          },
        }
      : {}),
  };
}
